import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, Gift, Loader2 } from 'lucide-react';
import '../styles/pull-a-card.css';

const API = process.env.REACT_APP_BACKEND_URL;

export default function GiftDeckRedeemPage() {
  const { code } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | pending | notfound
  const [gift, setGift] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/deck-gift/${code}`);
        if (res.status === 404) {
          if (active) setState('notfound');
          return;
        }
        const data = await res.json();
        if (!active) return;
        if (data.status === 'ready') {
          setGift(data);
          setState('ready');
        } else {
          setState('pending');
        }
      } catch {
        if (active) setState('notfound');
      }
    })();
    return () => {
      active = false;
    };
  }, [code]);

  return (
    <section className="pac-page" data-testid="gift-redeem-page" style={{ minHeight: '90vh' }}>
      <div className="pac-stars" aria-hidden="true" />
      <header className="pac-hero" style={{ marginTop: '1rem' }}>
        <p className="pac-eyebrow"><Gift size={14} strokeWidth={2} /> A Gift For You</p>

        {state === 'loading' && (
          <p className="pac-sub" data-testid="gift-loading">
            <Loader2 size={18} className="animate-spin" style={{ display: 'inline' }} /> Unwrapping your gift…
          </p>
        )}

        {state === 'notfound' && (
          <>
            <h1 className="pac-title">Hmm, no gift here</h1>
            <p className="pac-sub" data-testid="gift-notfound">
              This gift link isn&apos;t valid. Double-check the link, or explore
              The Village yourself.
            </p>
            <div style={{ marginTop: '1.5rem' }}>
              <Link to="/pull-a-card" className="village-btn village-btn--primary">Pull a Free Card</Link>
            </div>
          </>
        )}

        {state === 'pending' && (
          <>
            <h1 className="pac-title">Almost ready…</h1>
            <p className="pac-sub" data-testid="gift-pending">
              This gift is still being prepared. Please refresh in a moment.
            </p>
          </>
        )}

        {state === 'ready' && gift && (
          <>
            <h1 className="pac-title" data-testid="gift-ready-title">
              {gift.sender ? `${gift.sender} sent you a gift` : 'You received a gift'}
            </h1>
            <p className="pac-sub" style={{ marginBottom: '0.5rem' }}>
              {gift.recipient ? `${gift.recipient}, ` : ''}you&apos;ve been gifted{' '}
              <strong style={{ color: '#f8e7b0' }}>{gift.product_name}</strong>.
            </p>
            {gift.message && (
              <blockquote
                data-testid="gift-message"
                style={{
                  margin: '1.25rem auto',
                  maxWidth: '440px',
                  padding: '1rem 1.25rem',
                  borderLeft: '3px solid #d4af37',
                  background: 'rgba(212,175,55,0.06)',
                  borderRadius: '8px',
                  color: 'rgba(245,236,215,0.9)',
                  fontStyle: 'italic',
                  textAlign: 'left',
                }}
              >
                “{gift.message}”
              </blockquote>
            )}

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.6rem',
                justifyContent: 'center',
                marginTop: '1.5rem',
              }}
            >
              {gift.download_url && (
                <a
                  href={gift.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="village-btn village-btn--primary"
                  data-testid="gift-download-btn"
                >
                  <Download size={16} strokeWidth={2.2} /> Download Your Gift
                </a>
              )}
              {gift.download_url_epub && (
                <a
                  href={gift.download_url_epub}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="village-btn village-btn--secondary"
                  data-testid="gift-download-epub-btn"
                >
                  <Download size={16} strokeWidth={2.2} /> EPUB
                </a>
              )}
            </div>

            {gift.downloads && (
              <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '440px', marginInline: 'auto' }}>
                {gift.downloads.map((bk, i) => (
                  <div key={i} style={{ padding: '0.85rem 1rem', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '10px', textAlign: 'left' }}>
                    <p style={{ color: '#f8e7b0', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>{bk.title}</p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <a href={bk.pdf} target="_blank" rel="noopener noreferrer" download className="village-btn village-btn--primary" style={{ fontSize: '0.8rem' }} data-testid={`gift-bundle-pdf-${i}`}>PDF</a>
                      <a href={bk.epub} target="_blank" rel="noopener noreferrer" download className="village-btn village-btn--secondary" style={{ fontSize: '0.8rem' }} data-testid={`gift-bundle-epub-${i}`}>EPUB</a>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p style={{ marginTop: '2rem' }}>
              <Link to="/pull-a-card" className="village-btn village-btn--secondary">Pull a Free Card Too</Link>
            </p>
          </>
        )}
      </header>
    </section>
  );
}
